import { Location } from '@angular/common';
import { AfterViewInit, Directive, Host, Input, OnDestroy } from '@angular/core';
import { MatTabGroup } from '@angular/material/tabs';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Directive({
    selector: '[ndt-tab-router]',
    standalone: false
})
export class NdtTabRouterDirective implements AfterViewInit, OnDestroy {
   @Input() useTimeout = true;

   @Input() loadSuccess$: Observable<any>;

   private destroyed$ = new Subject<void>();

   constructor(
      private route: ActivatedRoute,
      private router: Router,
      private location: Location,
      @Host() private tabGroup: MatTabGroup
   ) {}

   ngAfterViewInit(): void {
      if (this.loadSuccess$) {
         this.loadSuccess$.pipe(takeUntil(this.destroyed$)).subscribe(() => {
            this.handleLoadSuccess();
         });
      }
   }

   private handleLoadSuccess(): void {
      if (this.useTimeout) {
         setTimeout(() => {
            this.initializeTabSelection();
         });
      } else {
         this.initializeTabSelection();
      }

      this.tabGroup.selectedIndexChange.pipe(takeUntil(this.destroyed$)).subscribe((index) => {
         const tabName = this.formatTabName(this.tabGroup._allTabs.toArray()[index].textLabel);
         const currentUrl = this.router.url.split('?')[0];
         const segments = currentUrl.split('/').filter(Boolean);
         const existingTabNames = this.tabGroup._allTabs.toArray().map((tab) => {
            return this.formatTabName(tab.textLabel);
         });
         const lastSegment = segments[segments.length - 1];
         const newSegments = existingTabNames.includes(lastSegment)
            ? [...segments.slice(0, -1), tabName]
            : [...segments, tabName];

         const newUrl = '/' + newSegments.join('/');
         this.location.replaceState(newUrl);
      });
   }

   private initializeTabSelection(): void {
      if (this.route.snapshot.params['tab']) {
         const allTabs = this.tabGroup._allTabs.toArray();
         const tabIndex = allTabs.findIndex(
            (t) => this.formatTabName(t.textLabel) === this.route.snapshot.params['tab']
         );

         if (tabIndex !== -1) {
            const originalAnimationDuration = this.tabGroup.animationDuration;
            this.tabGroup.animationDuration = '0ms';
            setTimeout(() => {
               this.tabGroup.animationDuration = originalAnimationDuration;
            });
            this.tabGroup.selectedIndex = tabIndex;
         } else {
            this.router.navigate(['/not-found'], { skipLocationChange: true });
         }
      }
   }

   private formatTabName(tabName: string): string {
      return tabName.toLowerCase().replace(/\s+/g, '');
   }

   ngOnDestroy(): void {
      this.destroyed$.next();
   }
}
